import { ChartOptions } from "chart.js";
import { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { checkCustomSATEnabled, formatDateForWidgets } from "../../../utils";
import { ScoreGraphData, ScoreWidgetProps, TestResult } from "./dataTypes";

const ScoreWidget = ({ overallResult,scoreType }: ScoreWidgetProps) => {
    const [enabled, setEnabled] = useState(true);
    const [chartData, setChartData] = useState<ScoreGraphData>({
      labels: [],
      datasets: [],
    });
  
    const convertData = (data: TestResult[], chartTitle: string) => {
      let requiredData: ScoreGraphData = {
        labels: [],
        datasets: [],
      };
  
      if (!data) {
        return requiredData;
      }

      !checkCustomSATEnabled() && (data = data.filter((item) => !item.isCustomTest))
  
      data.forEach((item) => {
        const formattedDate = formatDateForWidgets(item.testtakendate);
        requiredData.labels.push(formattedDate);
      });
  
      let readingAndWritingData;
      let mathematicsData;
      let totalScoreData;
  
      if (chartTitle === "RawScore") {
        readingAndWritingData = data.map(
          (item) => item.scores.readingAndWritingRawScore
        );
        mathematicsData = data.map((item) => item.scores.mathematicsRawScore);
        totalScoreData = data.map(
          (item) =>
            item.scores.readingAndWritingRawScore +
            item.scores.mathematicsRawScore
        );
      } else {
        readingAndWritingData = data.map(
          (item) => item.scores.readingAndWritingScaledScore
        );
        mathematicsData = data.map((item) => item.scores.mathematicsScaledScore);
        totalScoreData = data.map((item) => item.scores.totalScaledScore);
      }

      const customPointBorderColors = data.map((test) =>
        test.isCustomTest ? "#FFBA08" : undefined
      );
      const customPointBorderWidths = data.map((test) =>
        test.isCustomTest ? 2  : 0
      );
  
      requiredData.datasets = [
        {
          label: "R&W",
          data: readingAndWritingData,
          borderColor: "#8F3985",
          borderWidth: 2,
          pointBackgroundColor: "#8F3985",
          pointBorderColor: customPointBorderColors, 
          pointBorderWidth: customPointBorderWidths,
          tension: 0.0,
          fill: false,
        },
        {
          label: "Math",
          data: mathematicsData,
          borderColor: "#FF0035",
          borderWidth: 2,
          pointBackgroundColor: "#FF0035",
          pointBorderColor: customPointBorderColors,
          pointBorderWidth: customPointBorderWidths,
          tension: 0.0,
          fill: false,
        },
        {
          label: "Total Score",
          data: totalScoreData,
          borderColor: "#397367",
          borderWidth: 2,
          pointBackgroundColor: "#397367",
          pointBorderColor: customPointBorderColors,
          pointBorderWidth: customPointBorderWidths,
          tension: 0.0,
          fill: false,
        },
      ];
  
      return requiredData;
    };
    const options: ChartOptions<"line"> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: false,
            font: {
              size: 12,
              family: "Gully",
            },
            boxWidth: 14,
            boxHeight: 14,
            textAlign: "center",
            generateLabels: (chart) => {
              return chart.data.datasets.map((dataset, index) => ({
                text: dataset.label || "Unnamed Dataset",
                fillStyle: dataset.borderColor as string,
                hidden: !chart.isDatasetVisible(index),
                strokeStyle: dataset.borderColor as string,
                lineWidth: 1,
                datasetIndex: index,
              }));
            },
          },
        },
        tooltip: {
          callbacks: {
            title: (tooltipItem) => {
              const index = tooltipItem[0].dataIndex;
              const testName = overallResult[index]?.name;
              return `${testName}`;
            },
            label: (tooltipItem) => {
              const index = tooltipItem.dataIndex;
              const isCustom = overallResult[index]?.isCustomTest;
              return `${tooltipItem.dataset.label}: ${tooltipItem.raw}${isCustom ? ' - Custom Test': ''}`;
            },
          },
          titleFont: {
            family: "Gully",
            size: 14,
            weight: "bold",
          },
          bodyFont: {
            family: "Gully",
            size: 12,
          },
          footerFont: {
            family: "Gully",
            size: 12,
            style: "italic",
          },
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            color: "#000",
            align: "center",
            font:{
              size: 10,
              weight: "bold",
              family: "Gully",
            }
          },
          grid: {
            drawTicks: true,
            display: false,
          },
          title: {
            display: true,
            text: "Date",
            color: "#000",
            font: {
              size: 12,
              weight: "bold",
              family: "Gully",
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            display: true,
          },
          title: {
            display: true,
            text:  scoreType === 'RawScore'  ? "Raw score" : "Scaled score",
            color: "#000",
            font: {
              size: 12,
              weight: "bold",
              family: "Gully",
            },
          },
          ticks:{
            font:{
              size: 10,
              weight: "bold",
              family: "Gully",
            }
          }
        },
      },
    };
  
    useEffect(() => {
      if (enabled) {
        setChartData(convertData(overallResult, "ScaledScore"));
      } else {
        setChartData(convertData(overallResult, "RawScore"));
      }
    }, [enabled]);
  
    useEffect(() => {
      if(scoreType === 'RawScore'){
        setChartData(convertData(overallResult, "RawScore"))
      }
      else{
        setChartData(convertData(overallResult, "ScaledScore"))
      }
    }, [overallResult]);
  
    return (
      <div
        className="w-full flex flex-col p-3 justify-between rounded-[18px] border-[0.5px] border-satPrepGrey bg-satPrepTransWhite backdrop-blur-sevenFive shadow-test-card"
      >
        <p className="text-center font-gully text-xl font-medium text-satPrepDarkAccent">
          {scoreType === 'RawScore' ? 'Raw' : 'Scaled'} score progress
        </p>
        <div>
          {/* <div className="w-full flex justify-center items-center gap-6">
            <p className="font-gully text-xs font-semibold text-appPrimaryColor">
              RAW SCORE
            </p>
            <ToggleSwitch
                checked={enabled}
                onChange={() => setEnabled(!enabled)}
              />
            <p className="font-gully text-xs font-semibold text-[#283044]">
              SCALED SCORE
            </p>
          </div> */}
  
          <div className="w-full min-h-[300px] max-h-[400px]">
            <Line data={chartData} options={options} />
          </div>
        </div>
      </div>
    );
  };

export default ScoreWidget;